<template>
  <component
    v-on="$listeners"
    :is="to ? 'RouterLink' : 'button'"
    :class="[{ outline, loading, bold }, size, variant]" 
    :disabled="loading"
    :to="to"
  >
    <IconSpinner v-if="loading"/>
    <slot/>
  </component>
</template>


<script>
import IconSpinner from '@icons/IconSpinner';

export default {
  name: 'AppButton',

  components: {
    IconSpinner,
  },

  props: {
    variant: String,
    bold: Boolean,
    loading: Boolean,
    outline: Boolean,
    to: String,
    size: {
      type: String,
      default: 'big',
    },
  },
};
</script>


<style lang="scss" scoped>
button, a {
  width: 100%;
  outline: none;
  border: none;
  line-height: 1em;
  position: relative;
  text-align: center;
  user-select: none;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;

  &.bold {
    font-weight: bold;
    text-transform: uppercase;
  }

  &:not(.outline) {
    box-shadow: 0px 1px 1px 0px #fff4;
    background-color: var(--color-primary);
    color: var(--color-light);
    backdrop-filter: blur(40px);

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      padding: 2px;
      border-radius: inherit;
      background: linear-gradient(295deg, #fff, #fff0);
      -webkit-mask: 
        linear-gradient(#fff 0 0) content-box, 
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
              mask-composite: exclude;
    }
  }

  &.outline {
    background-color: var(--color-background);
    box-shadow: inset 0 0 0 3px currentColor;
    color: var(--color-primary);
  }

  &.big {
    min-height: 50px;
    border-radius: 25px;
    font-size: 20px;
    padding: 16px 20px;
    text-transform: uppercase;
  }

  &.small {
    min-height: 40px;
    border-radius: 20px;
    font-size: 16px;
    padding: 10px 14px;
  }

  &.gradient {
    background: transparent;
    color: var(--color-secondary);
    transition: backdrop-filter .2s ease;
    backdrop-filter: blur(6px);

    &::after {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      border-radius: inherit;
      background-size: 120% 120%;
      background-position: 100% 100%;
      transition: background-position .2s ease, backdrop-filter .2s ease;
      z-index: -1;
    }

    &:nth-last-of-type(3n + 1)::after {
      background-image: linear-gradient(320deg, #c7b2f780 0%, #d9f85580 50%, #f0724a80 90%);
    }

    &:nth-last-of-type(3n + 2)::after {
      background-image: linear-gradient(280deg, #d9f85580 10%, #c7b2f780 95%);
    }

    &:nth-last-of-type(3n + 3)::after {
      background-image: linear-gradient(110deg, #d9f85580 5%, #c7b2f780 50%, #f0724a80 90%);
    }

    @media (hover: hover) {
      &:hover {
        backdrop-filter:blur(10px) !important;
        &::after {
          background-position: 0 !important;
          backdrop-filter: sepia(1) !important;
        }
      }
    }
  }

  &:not(.gradient) {
    @media (hover: hover) {
      cursor: pointer;
      transition: filter .2s, transform .2s;
      &:hover {
        filter: sepia(.25);
        transform: scaleX(.99);
      }
    }
  }
}
</style>